import React, { useEffect, useState, useRef, useMemo } from 'react'
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'

import styles from './styles.module.scss'

import { showAssetForm } from '../../../store/asset/actions'
import { getCompanyAssets } from '../../../api'
import { formatLatestScan, getEstimatedCost, getPenalty } from '../utils'

import { PlusIcon, ThreeDotsIcon } from '../Icons'
import { useTranslation } from 'react-i18next'
import Table from '../../Invoices/Table'
import AssetStatus from '../AssetStatus'
import AssetTimeLeft from '../AssetTimeLeft'

const columnHelper = createColumnHelper()

const AssetRentals = ({ companyId }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { categories, ownerships, employees, places } = useSelector(state => state.asset.options)
  
  const [assets, setAssets] = useState([])

  useEffect(() => {
    getAssets()
  }, [companyId])

  const getAssets = async () => {
    const query = {
      last_scan: true,
      ownership: true,
      place: true,
      employee: true,
      category: true,
      ownership_code: 'rental'
    }
    const res = await getCompanyAssets(companyId, query)
    if (Array.isArray(res)) {
      setAssets(res)
    }
  }

  const handleCreateAsset = () => {
    const rentalOwnership = ownerships.find(ownership => ownership.code === 'rental')
    dispatch(showAssetForm({
      ownership_id: rentalOwnership?.id
    }))
  }

  const handleToggleActionMenu = (e, invoiceId) => {
    // e.stopPropagation()
    // const rect = e.currentTarget.getBoundingClientRect()
    // setActionMenuPosition({
    //   top: rect.bottom + 6,
    //   left: rect.right - 144,
    // })
    // setOpenActionMenuId(prev => (prev === invoiceId ? null : invoiceId))
  }

  const columns = useMemo(() => [
    columnHelper.display({
      id: 'asset',
      header: t('Asset'),
      cell: ({ row }) => {
        return (
          <div className={styles.assetCell}>
            <div className={styles.assetImageWrapper}>
              <img src={row.original.details.photo} className={styles.assetImage} />
            </div>
            <div>
              <div className={styles.assetName}>{row.original.name}</div>
              <div className={styles.assetCategory}>{row.original.code} · {row.original.category.name}</div>
            </div>
          </div>
        )
      }
    }),
    columnHelper.accessor('rental_detail.supplier', {
      enableSorting: false,
      header: t('Supplier'),
      cell: ({getValue, row}) => {
        return (
          <div>
            <b>{getValue()}</b>
            <div>{ row.original.rental_detail.supplier_reference }</div>
          </div>
        )
      }
    }),
    columnHelper.accessor('rental_detail.rental_start_date', {
      enableSorting: false,
      header: t('Rental start date'),
      cell: ({ getValue }) => {
        const value = getValue()
        return value ? moment(value).format('YYYY-MM-DD') : ''
      }
    }),
    columnHelper.accessor('rental_detail.rental_end_date', {
      enableSorting: false,
      header: t('Return deadline'),
      cell: ({ getValue }) => {
        const value = getValue()
        if (!value) {
          return ''
        }
        return (
          <div>
            <b>{moment(value).format('YYYY-MM-DD')}</b>
            <div>{moment(value).format('HH:mm')}</div>
          </div>
        )
      }
    }),
    columnHelper.display({
      id: 'time_left',
      enableSorting: false,
      header: t('Time left'),
      cell: ({ row }) => {
        return (
          <AssetTimeLeft rentalEndDate={row.original.rental_detail?.rental_end_date} />
        )
      }
    }),
    columnHelper.display({
      id: 'pricing',
      enableSorting: false,
      header: t('Pricing'),
      cell: ({ row }) => {
        return (
          <div>
            <b style={{ textTransform: 'capitalize' }}>{row.original.rental_detail?.pricing_model}</b>
            <div>{row.original.rental_detail?.rate}</div>
          </div>
        )
      }
    }),
    columnHelper.display({
      id: 'contract',
      enableSorting: false,
      header: t('Contract'),
      cell: ({ row }) => {
        return (
          <div>
            <b>{row.original.rental_detail?.contract_amount}</b>
          </div>
        )
      }
    }),
    columnHelper.display({
      id: 'estimated_cost',
      enableSorting: false,
      header: t('Est. cost'),
      cell: ({ row }) => {
        return (
          <div>
            <b>{getEstimatedCost(row.original.rental_detail)}</b>
            <div>{t('estimated')}</div>
          </div>
        )
      }
    }),
    columnHelper.accessor('rental_detail.penalty_per_day', {
      enableSorting: false,
      header: t('Penalty'),
      cell: ({ getValue, row }) => {
        return (
          <div>
            <b>{getPenalty(row.original.rental_detail)}</b>
            <div>{getValue()}/day</div>
          </div>
        )
      }
    }),
    columnHelper.accessor(row => row.employee ? `${row.employee.name} ${row.employee.surname}` : t('Unassigned'), {
      id: 'assigned',
      enableSorting: false,
      header: t('Assigned to'),
    }),
    columnHelper.display({
      enableSorting: false,
      header: t('Project / last location'),
      cell: ({ row }) => {
        return (
          <div>
            <b>{row.original.place?.name ?? t('No project')}</b>
            <div>{row.original.location ?? t('No location')}</div>
          </div>
        )
      }
    }),
    columnHelper.accessor('status', {
      enableSorting: false,
      header: t('Status'),
      cell: ({getValue}) => <AssetStatus status={getValue()} />
    }),
    columnHelper.display({
      id: 'actions',
      enableSorting: false,
      header: '',
      cell: ({ row }) => (
        <div className={styles.actionMenuWrapper} data-action-menu>
          <button
            type='button'
            className={styles.actionButton}
            onClick={e => handleToggleActionMenu(e, row.original.id)}
          >
            <ThreeDotsIcon />
          </button>
        </div>
      ),
    })
  ], [t])

  const filteredAssets = useMemo(() => {
      return assets
    }, [assets])

  const table = useReactTable({
    data: filteredAssets,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: row => String(row.id),
  })

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <div className={styles.headerTitle}>{t('Rented assets')}</div>
          <div className={styles.headerTotals}>{t('{{count}} assets currently on rent', { count: assets.length })}</div>
        </div>
        <button type="button" className={styles.button} onClick={handleCreateAsset}>
          <PlusIcon />
          { t('Add rented asset') }
        </button>
      </div>
      <Table table={table} columns={columns} headerClassName={styles.tableHeader} />
    </div>
  )
}

export default AssetRentals
